import { cn } from "@patient-watch/ui";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { ChoiceGroup } from "./components/ChoiceGroup";
import { useRatingKeyboard } from "./hooks/useRatingKeyboard";
import { isLabelledChoiceScale } from "./utils/isLabelledChoiceScale";
import { ratingAnchorLines } from "./utils/ratingAnchors";

interface RatingOptionLine {
    label: string;
    description?: string | null;
}

interface RatingOption extends RatingOptionLine {
    value: string;
    segments?: RatingOptionLine[] | null;
}

interface Props {
    options: RatingOption[];
    orientation: "vertical" | "horizontal";
    value: string | null;
    onChange: (value: string | null) => void;
    name: string;
}

const RatingOptions = ({ options, orientation, value, onChange, name }: Props) => {
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const asRadios = isLabelledChoiceScale(options);

    // Helper function to handle option selection
    const handleOptionSelect = (optionValue: string) => {
        if (optionValue === "") {
            onChange(null);
        } else {
            onChange(optionValue);
        }
    };

    const groupRef = useRatingKeyboard({
        options,
        onSelect: handleOptionSelect,
        enabled: !asRadios,
    });

    // Labelled answers (AOFAS, Oswestry, WOMAC) are a choice list. The score stays
    // in the stored value. A numeric scale, with a blank label or a label that is
    // the number, keeps the number on the control.
    if (asRadios) {
        const hasBlankValue = options.some((option) => option.value === "");
        const selected = value == null ? (hasBlankValue ? "" : null) : String(value);
        return (
            <div data-choice-question="" className="pt-2">
                <ChoiceGroup
                    name={name}
                    options={options.map((option) => ({
                        value: String(option.value),
                        label: option.label,
                        description: option.description,
                        segments: option.segments ?? undefined,
                    }))}
                    value={selected}
                    onChange={(next) => {
                        const chosen = Array.isArray(next) ? next[0] : next;
                        onChange(chosen == null || chosen === "" ? null : chosen);
                    }}
                    data-testid={`form-options-${name}`}
                />
            </div>
        );
    }

    const vertical = orientation === "vertical" || (isMobile && options.length > 5);

    // Vertical: highest to lowest (best option at top)
    // Horizontal: lowest to highest (small on left, big on right)
    const sortedOptions = [...options].sort((a, b) => {
        const aValue = a.value === "" ? -Infinity : Number(a.value);
        const bValue = b.value === "" ? -Infinity : Number(b.value);
        return vertical ? bValue - aValue : aValue - bValue;
    });

    // Helper function to check if an option is selected
    const isSelected = (optionValue: string) => {
        if (optionValue === "") {
            return value === null;
        }
        return value === optionValue;
    };

    // Helper function to display the option value
    const displayValue = (optionValue: string) => {
        if (optionValue === "") {
            return "N/A";
        }
        return optionValue;
    };

    if (vertical) {
        return (
            <div
                ref={groupRef}
                className="space-y-3 pt-2"
                role="radiogroup"
                aria-label={name}
                data-rating-keyboard=""
                data-testid={`form-options-${name}`}
            >
                {sortedOptions.map((option, index) => (
                    <button
                        type="button"
                        key={option.value}
                        data-testid="form-option"
                        data-input-id={name}
                        data-option-value={option.value}
                        onClick={() => handleOptionSelect(option.value)}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(null)}
                        className={cn(
                            "flex items-center gap-3 p-3 rounded-lg w-full transition-all",
                            "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500",
                            isSelected(option.value) && "bg-indigo-50"
                        )}
                    >
                        <div
                            className={cn(
                                "flex justify-center items-center rounded-full w-10 h-10 font-medium text-lg transition-colors",
                                isSelected(option.value)
                                    ? "bg-indigo-600 text-white"
                                    : "bg-white border-2 border-slate-300 text-slate-700"
                            )}
                        >
                            {displayValue(option.value)}
                        </div>
                        <span className="text-slate-700 text-sm text-left">{option.label}</span>
                    </button>
                ))}
            </div>
        );
    }

    // A 0-10 scale labelled "0 = None", "1 = Very mild", ...: the words go in a key under the scale, not under each button.
    const anchorLines = ratingAnchorLines(sortedOptions);

    return (
        <div
            ref={groupRef}
            className="space-y-8 pt-4"
            role="radiogroup"
            aria-label={name}
            data-rating-keyboard=""
            data-testid={`form-options-${name}`}
        >
            {/* Rating buttons */}
            <div className="flex items-start gap-0.5 px-1">
                {sortedOptions.map((option, index) => (
                    <div key={option.value} className="flex flex-col flex-1 items-center gap-2 min-w-0">
                        <button
                            type="button"
                            data-testid="form-option"
                            data-input-id={name}
                            data-option-value={option.value}
                            onClick={() => handleOptionSelect(option.value)}
                            onFocus={() => setFocusedIndex(index)}
                            onBlur={() => setFocusedIndex(null)}
                            className={cn(
                                "flex justify-center items-center rounded-full w-full max-w-12 aspect-square text-base sm:text-lg transition-all",
                                "hover:bg-indigo-50 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                                isSelected(option.value)
                                    ? "bg-indigo-600 hover:text-indigo-600 text-white shadow-md scale-110"
                                    : "bg-white border-2 border-slate-300 text-slate-700",
                                focusedIndex === index && "ring-2 ring-indigo-500 ring-offset-2"
                            )}
                        >
                            {displayValue(option.value)}
                        </button>
                        {option.label && !anchorLines && (
                            <span className="w-full text-slate-600 text-xs text-center break-words">
                                {option.label}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            {/* Visual scale line - fills from left (low to high) */}
            <div className="relative mx-6">
                <div className="top-1/2 absolute bg-slate-200 rounded-full w-full h-1 -translate-y-1/2" />
                <div
                    className="top-1/2 left-0 absolute bg-indigo-600 rounded-full h-1 -translate-y-1/2"
                    style={{
                        width: value ? `${(Number(value) / (sortedOptions.length - 1)) * 100}%` : "0%",
                        transition: "width 0.3s ease-in-out",
                    }}
                />
            </div>

            {anchorLines ? (
                <div
                    className="flex justify-between gap-4 -mt-3 px-1 text-slate-600 text-sm"
                    data-testid="rating-anchors"
                >
                    <span className="flex flex-col">
                        {anchorLines.low.map((line) => (
                            <span key={line}>{line}</span>
                        ))}
                    </span>
                    <span className="flex flex-col text-right">
                        {anchorLines.high.map((line) => (
                            <span key={line}>{line}</span>
                        ))}
                    </span>
                </div>
            ) : null}
        </div>
    );
};

export default RatingOptions;
